import { useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import classNames from 'classnames';

/* Client Store */
import { useHomeHubStoreActions, useHomeHubStoreUserFlowAttributes } from '@scenes/HomeHub/store/client/homehub.store';

/* Query Selectors & Hooks */
import useRouteController from '@scenes/HomeHub/hooks/hook.routeController';
import { useUserIdSelector } from '@store/queries/user/useUser.selector';
import useSessionStorage, { SESSION_STORAGE_TYPES } from '@hooks/useSessionStorage';

/* Components */
// import SurveyLandingPage from 'src/scenes/HomeHub/components/SurveyInterstitialLanding';
// import Loader from 'src/scenes/HomeHub/components/Loader';

/* Utils & Constants */
import { inActiveSurveySession, SURVEY_TYPES } from '@scenes/HomeHub/homehub.constants';
import { isBrowser, keydownCapture } from '@utils/ui';

import styles from '@scenes/HomeHub/homeHub.module.scss';
import { SegmentTrackingAction, TRACKING_TYPES } from '@integrations/segment/homehub';

/* Component Constants */
// const UserNameStep = dynamic(() => import('@scenes/HomeHub/pages/User/User.Name'), {ssr: false});
const UserGreetingStep = dynamic(() => import('@scenes/HomeHub/pages/User/User.Greeting'), { ssr: false });
const UserEmailStep = dynamic(() => import('@scenes/HomeHub/pages/User/User.Email'), { ssr: false });
const UserPasswordStep = dynamic(() => import('@scenes/HomeHub/pages/User/User.Existing.EnterPassword'), { ssr: false });
const UserSetPasswordStep = dynamic(() => import('@scenes/HomeHub/pages/User/User.Existing.SetPassword'), { ssr: false });
const UserSetPasswordRegisterStep = dynamic(() => import('@scenes/HomeHub/pages/User/User.New.PasswordRegsiter'), { ssr: false });
const UserEnterPasswordFromEmailStep = dynamic(() => import('@scenes/HomeHub/pages/User/User.Existing.SetPassword.Confirm'), { ssr: false });

/**
 * UserInformationSurvey
 * User personal flow for the surveys
 *
 * @returns {JSX.Element}
 * @constructor
 */

/* Screen types */
export const UserScreenTypes = {
  name: 'name',
  greeting: 'greeting',
  email: 'email',
  password: 'password',
  setPassword: 'setPassword',
  setPasswordRegister: 'setPasswordRegister',
  emailPassword: 'emailPassword',
};

export type TUserPageProps = {
  userSegmentTracking: BaseAnyFunction;
};

const UserInformationSurvey = () => {
  const { setAttributeInUserFlow } = useHomeHubStoreActions();
  const { userEmail, firstName: fName } = useHomeHubStoreUserFlowAttributes();

  /* Hooks */
  const router = useRouter();
  const sessionStorage = useSessionStorage(SESSION_STORAGE_TYPES.sessionStorage);

  /* Constants */
  const { step, email: queryEmail, firstName: queryName } = router.query;
  const { nextUserRoute } = useRouteController();

  /* Query Selectors */
  const userID = useUserIdSelector();

  /**
   * Each Base page needs to tell the Interstitial screen "where" to go.
   */
  // const startSurveyLink = () => {
  //   nextUserRoute(UserScreenTypes.name);
  // };

  const renderComponentByStep = ({ userSegmentTracking }: TUserPageProps) =>
    (() => {
      switch (step) {
        // case UserScreenTypes.name:
        // return <UserNameStep />; // removal for now. Skip name step
        case UserScreenTypes.greeting:
          return <UserGreetingStep />;
        case UserScreenTypes.name:
        case UserScreenTypes.email:
          return <UserEmailStep />;
        case UserScreenTypes.password:
          return <UserPasswordStep userSegmentTracking={userSegmentTracking} />;
        case UserScreenTypes.setPassword:
          return <UserSetPasswordStep />;
        case UserScreenTypes.setPasswordRegister:
          return <UserSetPasswordRegisterStep userSegmentTracking={userSegmentTracking} />;
        case UserScreenTypes.emailPassword:
          return <UserEnterPasswordFromEmailStep userSegmentTracking={userSegmentTracking} />;
        default:
          return <UserEmailStep />;
        // return <SurveyLandingPage startSurveyLink={startSurveyLink} surveyType={SURVEY_TYPES.user} />;
      }
    })();

  /* Determine Redirect: make sure users steps align with what they filled out */
  const determineRedirect = useCallback(() => {
    const iterateAndFind = (qParams: string[]) => qParams.some(qp => (step as string)?.toLowerCase().includes(qp));
    /* rethinking the naming. perhaps move the component register to store, then just read that */
    // if (!userID && iterateAndFind(['email', 'greeting']) && !fName) nextUserRoute(UserScreenTypes.name);
    // if (!userID && iterateAndFind(['password']) && !userEmail) nextUserRoute(UserScreenTypes.name);

    if (!userID && iterateAndFind(['password']) && !userEmail) nextUserRoute(UserScreenTypes.email);
  }, [userID, userEmail, step, nextUserRoute]);

  /* Tracking */
  const userSegmentTracking = useCallback(
    (trackType: string) => {
      SegmentTrackingAction.homeHubTracking(
        {
          user_id: userID,
          user_name: fName as string,
          user_email: userEmail as string,
          user_step: (step || UserScreenTypes.email) as string,
          survey_name: SURVEY_TYPES.user,
        },
        trackType,
      );
    },
    [userID, fName, userEmail, step],
  );

  /* If we are in a password step, but no user email, direct back to beginning. */
  useEffect(() => {
    determineRedirect();
    /* Track the user thru the user flow */
    userSegmentTracking(step ? TRACKING_TYPES.surveyQuestionStarted : TRACKING_TYPES.surveyStarted);

    return () => {
      if (step) {
        userSegmentTracking(TRACKING_TYPES.surveyQuestionFinished);
      }
    };
  }, [step, determineRedirect, userSegmentTracking]);

  /* Email/Name in url, set those in the local store for consistancy sake & forward on */
  useEffect(() => {
    if (queryEmail && queryName) {
      setAttributeInUserFlow({ email: queryEmail as string, firstName: queryName as string });
      nextUserRoute(UserScreenTypes.email);
    }
  }, [queryEmail, queryName, determineRedirect, userSegmentTracking, nextUserRoute, setAttributeInUserFlow]);

  useEffect(() => {
    if (isBrowser()) {
      window.addEventListener('keydown', keydownCapture, true);
    }

    return () => {
      if (isBrowser()) {
        window.removeEventListener('keydown', keydownCapture, true);
      }
    };
  }, [nextUserRoute, setAttributeInUserFlow]);

  /* User is already logged in: Move them to the surveyType */
  useEffect(() => {
    /* User is active, don't show the welcome back notification */
    sessionStorage.setSessionStorage({ key: inActiveSurveySession, value: '1' });
  }, [userID, sessionStorage]);

  const layoutStyles = classNames(styles.user_layout, {
    [styles.superstitial]: router.query.step === undefined,
  });

  /* Lets wait till user is available */
  // if (!hasUser) return <Loader />;

  return (
    <div className={layoutStyles}>
      <div className={styles.user_component}>{renderComponentByStep({ userSegmentTracking })}</div>
    </div>
  );
};

export default UserInformationSurvey;
